import React, { useMemo, useRef, useState } from 'react';
import ModernSelect from 'src/components/ModernSelect';
import { variables } from 'src/Tenants';
import { SuiteDummyData, SuiteHeaders } from 'src/common/constants';
import { CSVLink } from 'react-csv';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadUnitsCSV,
  UseDownloadUnitsCSVReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadUnitsCSV/useDownloadUnitsCSV';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVAndPDFParserOverlay/DownloadCSVAndPDFParserOverlay';
import HomeownerImportDialogue from 'src/pages/Property/HomeOwnerImportDialogue/HomeownerImportDialogue';
import UnitImportDialogue from 'src/pages/Suite/Components/UnitImportDialogue/UnitImportDialogue';

interface ActionOption {
  value: string;
  label: string;
  disabled?: boolean;
}

const filterActionOptionsByRole = (
  userRole: string,
  allOptions: Array<ActionOption>
): ActionOption[] => {
  if (userRole === 'Admin' || userRole === 'Super Admin') {
    return allOptions;
  }

  return allOptions.filter(
    (option: ActionOption) =>
      option.value !== 'bulk-import' && option.value !== 'bulk-export'
  );
};

const SuitesActionSelect = ({
  user,
  property,
  onAddSuite,
  handleOpenBulkWalkthroughsDialogue,
  handleOpenBulkKeyPickupDialogue,
  handleOpenBulkActivateWalkthroughsDialogue,
  fetchPropertySuites,
  filteredSuiteData,
}: any) => {
  const [isHomeownerImportOpen, setIsHomeownerImportOpen] = useState(false);
  const [isUnitImportOpen, setIsUnitImportOpen] = useState(false);
  const [isUnitsExportOpen, setIsUnitsExportOpen] = useState(false);

  const userRole: any = user?.roles[0].role?.name;

  const csvDownloadRef = useRef<any>(null);

  const unitsCsvDownload: UseDownloadUnitsCSVReturn = useDownloadUnitsCSV();

  const onDownloadUnitsCSV = async (
    dateFormat: CsvDateFormat
  ): Promise<boolean> => {
    return await unitsCsvDownload.onDownloadCSV(property?.id, dateFormat);
  };

  const shouldHideButtons: boolean =
    user.roles[0].role.name === 'Home Owner' ||
    user.roles[0].role.name === 'Occupant' ||
    user.roles[0].role.name === 'Strata Manager';

  if (shouldHideButtons) {
    return <></>;
  }

  const allActionOptions: ActionOption[] = [
    { value: 'bulk-import', label: 'Import Units' },
    { value: 'bulk-export', label: 'Export Units' },
    { value: 'add-suite', label: `Add ${variables.Suite}` },
    { value: 'bulk-walkthroughs', label: 'Add Walkthroughs' },
    { value: 'bulk-activate-walkthroughs', label: 'Activate Walkthroughs' },
    { value: 'bulk-key-pickup', label: 'Configure Key Pickups' },
    { value: 'import-homeowners', label: 'Import Users' },
  ];

  const filteredActionOptionsByRole: ActionOption[] = useMemo(
    () => filterActionOptionsByRole(userRole, allActionOptions),
    [userRole]
  );

  const handleActionChange = async (option: ActionOption | null) => {
    const action = option?.value;

    switch (action) {
      case 'bulk-export':
        setIsUnitsExportOpen(true);
        break;
      case 'download-csv':
        triggerCSVDownload();
        break;
      case 'bulk-import':
        setIsUnitImportOpen(true);
        break;
      case 'add-suite':
        onAddSuite();
        break;
      case 'bulk-walkthroughs':
        handleOpenBulkWalkthroughsDialogue();
        break;
      case 'import-homeowners':
        setIsHomeownerImportOpen(true);
        break;
      case 'bulk-activate-walkthroughs':
        handleOpenBulkActivateWalkthroughsDialogue();
        break;
      case 'bulk-key-pickup':
        handleOpenBulkKeyPickupDialogue();
        break;
      default:
        break;
    }
  };

  const triggerCSVDownload = () => {
    if (csvDownloadRef.current?.link) {
      csvDownloadRef.current.link.click();
    }
  };

  return (
    <div className="button-right">
      <ModernSelect<ActionOption>
        value={null}
        options={filteredActionOptionsByRole}
        onChange={handleActionChange}
        placeholder="Quick Actions..."
        isSearchable={false}
        size="regular"
      />
      {/* Hidden CSV Download Link */}
      <div style={{ display: 'none' }}>
        <CSVLink
          ref={csvDownloadRef}
          data={SuiteDummyData}
          headers={SuiteHeaders}
          filename={`${variables.suiteName}.csv`}
          target="_blank"
          data-csv-download="true"
        >
          Download CSV
        </CSVLink>
      </div>
      <UnitImportDialogue
        isOpen={isUnitImportOpen}
        onClose={() => setIsUnitImportOpen(false)}
        onImportComplete={() => fetchPropertySuites(property?.id)}
        propertyId={property?.id}
        userId={user.id}
      />
      <DownloadCSVAndPDFParserOverlay
        isOpen={isUnitsExportOpen}
        onClose={() => setIsUnitsExportOpen(false)}
        onDownloadCSV={onDownloadUnitsCSV}
        isCSVLoading={unitsCsvDownload.isLoading}
      />
      <HomeownerImportDialogue
        isOpen={isHomeownerImportOpen}
        onClose={() => setIsHomeownerImportOpen(false)}
        onImportComplete={() => fetchPropertySuites(property?.id)}
        filteredSuiteData={filteredSuiteData}
      />
    </div>
  );
};

export default SuitesActionSelect;
